// src/features/reconciliation/components/ReconciliationPage.tsx
//
// «Reconciliation view» (w7.u5, CF S6 "Show ... reconciliation (to finance)")
// — route /reconciliation. `GET /api/v2/ui/reconciliation` (w7.u1) returns
// every registered `fin.* id (02-metric-catalogue.md S2.4) for one property
// and period; this unit lists them generically in one panel rather than
// inventing named sub-sections the design docs never specify — same posture
// w7.u2's own report took for its superset panels.
import React from 'react';
import { Box } from '@mui/material';
import { useDataProvider, useLocaleState, useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { PeriodPicker } from '@/components/common/PeriodPicker';
import { useAnalyticsPeriod } from '@/providers/analyticsPeriodProvider';
import { useV2PropertySelection } from '@/hooks/useV2PropertySelection';
import { useAnalyticsFetch } from '@/hooks/useAnalyticsFetch';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { PropertySelector } from '@/features/insight-v2-shared/components/PropertySelector';
import { PropertyState } from '@/features/insight-v2-shared/components/PropertyState';
import { MetricPanelSection } from '@/features/insight-v2-shared/components/MetricPanelSection';
import { metricExportText, metricLabel } from '@/lib/v2MetricView';
import type { MetricResultDto } from '@/types/analyticsV2';

export const ReconciliationPage: React.FC = () => {
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const dataProvider = useDataProvider();
  const property = useV2PropertySelection('reconciliation');
  const exportCsv = useCsvExport({ page: 'reconciliation' });
  const canExport = hasResourcePermission('reconciliation', 'export');

  const { propertyId } = property;
  const [period, setPeriod] = useAnalyticsPeriod();

  const { data, loading, error, refresh } = useAnalyticsFetch<MetricResultDto[]>(
    () => (dataProvider as any).getAnalyticsReconciliation({ property: propertyId, from: period.from, to: period.to }),
    [dataProvider, propertyId, period.from, period.to],
    Boolean(propertyId)
  );

  const handleExport = async () => {
    if (!data) return;
    await exportCsv(
      data,
      [
        { labelKey: 'resources.insightV2.export.metric', value: (r: MetricResultDto) => metricLabel(r, translate) },
        { labelKey: 'resources.insightV2.export.value', value: (r: MetricResultDto) => metricExportText(r, translate, locale) },
      ],
      { from: period.from, to: period.to }
    );
  };

  return (
    <Box>
      <SectionHeader manualScreen="reconciliation"
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <PropertySelector selection={property} />
            <PeriodPicker value={period} onChange={setPeriod} showCompare={false} />
          </Box>
        }
        subtitle={translate('resources.reconciliation.subtitle')}
        helpConfig={{
          titleKey: 'resources.reconciliation.help.title',
          descriptionKey: 'resources.reconciliation.help.description',
          featureKeys: ['resources.reconciliation.help.features.list'],
          tipKeys: ['resources.reconciliation.help.tips.property'],
        }}
        showExport={canExport}
        onExportClick={handleExport}
      />

      {!propertyId ? (
        <PropertyState selection={property} />
      ) : (
        <>
          {error && <FetchErrorBanner onRetry={refresh} />}
          {/* P01: nothing here is checked against the hotel's own system yet; the panel says so. */}
          <MetricPanelSection
            title={translate('resources.reconciliation.help.title')}
            description={translate('resources.reconciliation.provenance')}
            metrics={data ?? []}
            loading={loading}
          />
        </>
      )}
    </Box>
  );
};

export default ReconciliationPage;
